import { useState } from "react";
import SvgMapper from "../../../features/common/SvgMapper";
import "../../Style.css";
import * as XLSX from "xlsx";

const CustomQuestions = ({ onChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricError, setRubricError] = useState("");
  const [fileError, setFileError] = useState("");

  const handleRemoveFile = (fileName) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
    setFileError("");
  };

  const handleRemoveRubric = () => {
    setRubricFile(null);
    setRubricError("");
    onChange({
      target: { name: "rubric_url", value: "" },
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const validateCustomQuestions = async (file) => {
    try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // 1. Only one sheet allowed
    if (workbook.SheetNames.length !== 1) {
      return "Only one worksheet is allowed for custom questions.";
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    if (!sheet || !sheet["!ref"]) {
      return "The uploaded Excel file is empty.";
    }

    const rows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      blankrows: false,
    });

    // 2. Empty sheet
    if (rows.length === 0) {
      return "The uploaded Excel file is empty.";
    }

    // 3. Validate header
    const header = String(rows[0][0] || "").trim().toLowerCase();

    if (header !== "question") {
      return 'Invalid template. Expected column name "question".';
    }

    // 4. Check for extra columns
    if (rows[0].filter((cell) => String(cell ?? "").trim() !== "").length > 1) {
      return 'Invalid template. Only the "question" column is allowed.';
    }

    // 5. No questions
    if (rows.length <= 1) {
      return "No questions found in the uploaded file.";
    }

    const seen = new Set();

    // 6. Validate each question
    for (let i = 1; i < rows.length; i++) {
      const question = String(rows[i][0] ?? "").trim();

      if (!question) {
        return `Question cannot be empty (Row ${i + 1}).`;
      }

      if (question.length > 500) {
        return `Question at Row ${i + 1} exceeds the maximum allowed length.`;
      }

      const key = question.toLowerCase();

      if (seen.has(key)) {
        return `Duplicate question found at Row ${i + 1}.`;
      }

      seen.add(key);
    }

    return null;
    } catch (error) {
      console.error(error);
      return "Unable to read the Excel file. Please upload a valid .xlsx file.";
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (files.length > 1) {
      setFileError("Please upload only one Excel file.");
      e.target.value = "";
      return;
    }

    const file = files[0];

    // Validate extension
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setFileError("Only .xlsx files are allowed for custom questions upload.");
      e.target.value = "";
      return;
    }

    // Validate Excel contents
    const validationError = await validateCustomQuestions(file);

    if (validationError) {
      setFileError(validationError);
      setSelectedFiles([]);
      e.target.value = "";
      return;
    }

    setFileError("");
    setSelectedFiles(files);

    try {
      let uploadedUrl = await uploadToS3(file);

      if (!uploadedUrl) {
        throw new Error("Failed to upload file to S3.");
      }

      onChange({
        target: { name: "additionalData", value: uploadedUrl },
      });
    } catch (error) {
      console.error("Upload error:", error);
      setFileError("Failed to upload file. Please try again.");
      setSelectedFiles([]);
    }
  };

  const validateRubric = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // -----------------------------
    // 1. Validate sheet count
    // -----------------------------
    const expectedSheets = ["Pre-checks", "Behavioral", "Conceptual"];

    if (workbook.SheetNames.length !== 3) {
      return "Rubric must contain exactly 3 sheets.";
    }

    for (const sheet of expectedSheets) {
      if (!workbook.SheetNames.includes(sheet)) {
        return `Missing sheet: ${sheet}`;
      }
    }

    // -----------------------------
    // 2. Expected headers
    // -----------------------------
    const expectedHeaders = {
      "Pre-checks": {
        A2: "PARAMETER",
        B2: "Category",
        C1: "SCORE",
        E1: "Notes",
        H1: "Comments",
      },

      Behavioral: {
        B1: "PARAMETER",
        C1: "Category",
        D1: 0,
        E1: 1,
        F1: 2,
        G1: 3,
        H1: 4,
        I1: "Comments",
      },

      Conceptual: {
        B1: "PARAMETER",
        C1: "Category",
        D1: 0,
        E1: 1,
        F1: 2,
        G1: 3,
        H1: 4,
        I1: "Comments",
      },
    };

    // -----------------------------
    // 3. Validate each sheet
    // -----------------------------
    for (const sheetName of expectedSheets) {
      const worksheet = workbook.Sheets[sheetName];

      if (!worksheet) {
        return `Missing sheet: ${sheetName}`;
      }

      // Check headers
      const headers = expectedHeaders[sheetName];

      for (const [cell, expectedValue] of Object.entries(headers)) {
        const actualValue = worksheet[cell]?.v;

        if (String(actualValue).trim() !== String(expectedValue).trim()) {
          return `${sheetName}: Invalid header at ${cell}. Expected "${expectedValue}".`;
        }
      }

      // Check minimum rows
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        blankrows: false,
      });

      // Remove completely empty rows
      const nonEmptyRows = rows.filter((row) =>
        row.some((cell) => String(cell ?? "").trim() !== "")
      );

      if (nonEmptyRows.length < 2) {
        return `${sheetName} must contain at least 2 rows.`;
      }
    }

    return null;
  } catch (error) {
    console.error(error);
    return "Invalid rubric file.";
  }
};

  const handleRubricChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setRubricError("Only .xlsx files are allowed for rubric upload");
      e.target.value = "";
      return;
    }

    const validationError = await validateRubric(file);

    if (validationError) {
      setRubricError(validationError);
      e.target.value = "";
      setRubricFile(null);
      return;
    }

    setRubricError("");
    setRubricFile(file);

    try {
      const url = await uploadToS3(file);
      if (url) {
        onChange({
          target: { name: "rubric_url", value: url },
        });
      }
    } catch (error) {
      console.error("Rubric upload error:", error);
      setRubricError("Failed to upload rubric file. Please try again.");
      setRubricFile(null);
    }
  };

  const uploadToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "ai-bot");
      formData.append("file", file);

      const timestamp = Date.now();
      const response = await fetch(
        `https://testservice.kraftshala.com/storage/add-external?timestamp=${timestamp}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      return null;
    }
  };

  return (
    <div className="custom-questions-section">
      <div className="custom-questions-header">
        <div className="custom-questions-number-badge-blue">
          3
        </div>
        <h3 className="custom-questions-title">
          Custom Questions
        </h3>
        <span className="custom-questions-subtitle">
          (Optional - Upload Google Sheets)
        </span>
      </div>

      <div className="custom-questions-upload-area-blue">
        <div className="custom-questions-upload-content">
          <div className="custom-questions-icon-container">
            {SvgMapper.spreadSheet("w-12 h-12 text-gray-400")}
          </div>

          <div>
            <h4 className="custom-questions-upload-heading">
              Upload Custom Questions
            </h4>
            <p className="custom-questions-upload-description">
              Upload a .xlsx file with custom questions for this interview
            </p>

            <div className="custom-questions-badge-container">
              <span className="custom-questions-badge">
                .xlsx
              </span>
            </div>

            <input
              type="file"
              id="custom-questions-upload"
              accept=".xlsx"
              className="custom-questions-file-input"
              onChange={handleFileChange}
            />

            <label
              htmlFor="custom-questions-upload"
              className="custom-questions-file-button"
            >
              {SvgMapper.fileIcon("w-4 h-4 mr-2")}
              Choose File
            </label>
            {fileError && (
              <p className="custom-questions-error">{fileError}</p>
            )}
          </div>
        </div>
      </div>

      {selectedFiles?.length > 0 &&
        selectedFiles?.map((file) => (
          <div
            key={file.name}
            className="custom-questions-file-item"
          >
            <div className="custom-questions-file-item-left">
              <div className="custom-questions-file-icon-green">
                {SvgMapper.spreadSheet("w-6 h-6 text-green-600")}
              </div>
              <div>
                <p className="custom-questions-file-name">
                  {file?.name}
                </p>
                <p className="custom-questions-file-size">
                  {formatFileSize(file?.size)}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleRemoveFile(file.name)}
              className="custom-questions-remove-button"
              title="Remove file"
            >
              ✕
            </button>
          </div>
        ))}

      <div className="custom-questions-rubric-header">
        <div className="custom-questions-number-badge-purple">
          4
        </div>
        <h3 className="custom-questions-title">
          Upload Rubric <span className="custom-questions-asterisk">*</span>
        </h3>
      </div>

      <div className="custom-questions-upload-area-purple">
        <div className="custom-questions-upload-content">
          <div className="custom-questions-icon-container">
            {SvgMapper.spreadSheet("w-12 h-12 text-gray-400")}
          </div>

          <div>
            <h4 className="custom-questions-upload-heading">
              Upload Evaluation Rubric
            </h4>
            <p className="custom-questions-upload-description">
              Upload a .xlsx file containing the scoring rubric for this interview
            </p>

            <div className="custom-questions-badge-container">
              <span className="custom-questions-badge">
                .xlsx
              </span>
            </div>

            <input
              type="file"
              id="rubric-upload"
              accept=".xlsx"
              className="custom-questions-file-input"
              onChange={handleRubricChange}
            />

            <label
              htmlFor="rubric-upload"
              className="custom-questions-file-button"
            >
              {SvgMapper.fileIcon("w-4 h-4 mr-2")}
              Choose Rubric File
            </label>
            {rubricError && (
              <p className="custom-questions-error">{rubricError}</p>
            )}
          </div>
        </div>
      </div>

      {rubricFile && (
        <div className="custom-questions-file-item">
          <div className="custom-questions-file-item-left">
            <div className="custom-questions-file-icon-blue">
              {SvgMapper.spreadSheet("w-6 h-6 text-blue-600")}
            </div>
            <div>
              <p className="custom-questions-file-name">
                {rubricFile.name}
              </p>
              <p className="custom-questions-file-size">
                {formatFileSize(rubricFile.size)}
              </p>
            </div>
          </div>
          <button
            onClick={handleRemoveRubric}
            className="custom-questions-remove-button"
            title="Remove rubric file"
          >
            ✕
          </button>
        </div>
      )}

      <div className="custom-questions-info-box">
        <h4 className="custom-questions-info-heading">Format</h4>
        <div className="custom-questions-info-text">
         
          
          <a
            href="https://s3.ap-south-1.amazonaws.com/testconvexstorage.kraftshala.com/test/no-auth-uplaod//14i86yulejmjbqbg3b.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            className="custom-questions-info-link"
          >
            Download rubric Format Template
          </a>
        </div>
      </div>
    </div>
  );
};

export default CustomQuestions;
